import { Graphics, Ticker } from "pixi.js"
import { EmberMemberStatus } from "game-common/models"

// THE GROUND HALO: "this body, right there, needs you."
//
// The ember roster already answers "is anybody in trouble?" -- it is a docked strip with a red chip
// on it. The question the strip cannot answer is the one you have while standing in the camp looking
// at six people milling around a fire: WHICH of them, and is it worth walking over. This is the
// answer to that, and only to that.
//
// Division of labour, which is the whole reason this is as plain as it is:
//
//   the role glyph (avatar_hud)  what this person's JOB is          -- permanent, above the head
//   the roster pill              WHAT is wrong with them            -- a word, in the strip
//   this halo                    WHICH body it is wrong with        -- in the world, under the feet
//
// So the halo carries no information beyond "this one, and how badly". It never needs to be read,
// only noticed, which is what lets it be quiet enough to leave on all the time.
//
// WHY THE GROUND. The lane above the head is full -- the role glyph lives there permanently, with
// the nameplate, the HP meter, the sigil, the interaction pointer and chat bubbles all sharing its
// ~20px and a hand-tuned z-order. Ground level is free, never occludes a neighbour, and a camp with
// four members in trouble still parses; four more floating icons would not.
//
// NOT AN OUTLINE GLOW: that vocabulary is the hover highlight's ("you can act on this right now").
// Not a stroked ellipse either -- a thin cool-toned ring under the feet already means "a real player
// is driving this body" (see PlayerRing). This is a soft FILLED disc in a warm alarm colour, which
// is a different enough shape and hue that the two can't be confused. They also can never co-occur:
// memberStatus() returns undefined for the `player` and `lord` roles, so only members and visitors
// ever wear a halo, and only players ever wear a ring.
//
// ONE HUE, TWO INTENSITIES. The obvious design is the roster's own amber for a warning and red for an
// alarm, and it fails exactly where it has to work: the trouble happens AT THE FIRE and AT THE CHEST,
// and amber on firelit trodden dirt is nearly invisible. So severity is carried by alpha and by how
// deeply the halo breathes, not by colour, and the one colour is the roster's alarm red -- the same
// number, so a red chip in the strip and a red floor in the world are legibly one fact.

// Geometry in the sprite's own 64x64 art space, anchored by the sprite on the same foot line the
// shadow and the player ring use. A little wider than the ring so it reads as its own mark rather
// than as a fattened one.
const CORE_RX = 15
const CORE_RY = 7
// The softness, faked with a second larger fill at a fraction of the alpha. NOT a blur filter: a
// per-entity filter allocates a render target per body, which is exactly the kind of thing that
// costs frames on the low-end machines this game targets.
const HALO_RX = 20
const HALO_RY = 9.5
const HALO_ALPHA_SHARE = 0.45

// The roster's own ALARM red (roster_status.ts), restated rather than imported: sprites must not
// depend on gui (the import cycle the role glyph's emoji are duplicated to avoid). Keep in sync.
const ALARM_RED = 0xff5959
// The selection green. In the roster's palette green is the FORAGER's colour, which would normally
// rule it out -- but only ever ONE body wears this disc at a time, and it appears exactly when the
// player has just tapped a card, so there is no moment at which it could be read as a role. Nothing
// else in the ground vocabulary is green (the player ring is cool blue, the problem disc red), so
// "green circle under someone" can only mean "the card you are looking at is this person".
const SELECTED_GREEN = 0x7be08a

export type StatusHaloTier = "warn" | "alarm" | "selected"

interface TierTuning {
    /** Resting opacity of the core fill. */
    alpha: number
    /** How far the breath swings either side of `alpha`. */
    depth: number
    /** Breath period. */
    periodMs: number
}

export interface StatusHaloTuning {
    enabled: boolean
    /** The problem disc's colour, shared by both red tiers (see the one-hue note above). */
    color: number
    /** The roster selection disc's colour. */
    selectedColor: number
    /** Multiplier on every radius, for trying a tighter or wider footprint without a rebuild. */
    scale: number
    warn: TierTuning
    alarm: TierTuning
    selected: TierTuning
}

// DELIBERATELY SLOWER THAN THE ROSTER. The strip's chips pulse at 600ms (the fire warning) and its
// starving bars at 900ms, which is right for a 24px chip you flick your eyes to -- held constantly
// on a body in the world, that same rate reads as flashing, and the one thing this cue must not do
// is pull focus while you are chopping a tree. These periods are slow enough to read as breathing.
export const DEFAULT_STATUS_HALO_TUNING: StatusHaloTuning = {
    enabled: true,
    color: ALARM_RED,
    selectedColor: SELECTED_GREEN,
    scale: 1,
    // Hungry, on its way to the chest of its own accord: nothing is being lost yet, so this is barely
    // there -- you notice it when you look at the camp, not while you are looking away from it.
    warn: { alpha: 0.2, depth: 0.07, periodMs: 1800 },
    // Stuck, starving at an empty chest, or standing there with no job: the camp loses this member if
    // the player does nothing. Still not a flash -- twice as present, and breathing visibly faster.
    alarm: { alpha: 0.38, depth: 0.15, periodMs: 1100 },
    // The roster's selected card, answering "which of these people is that card" -- so it is DEAD
    // STILL (depth 0). The two red tiers breathe because something is wrong and is getting worse;
    // this is a standing fact about which body the player just pointed at, and a selection that
    // pulsed would read as a third alarm. `periodMs` is unused at depth 0 but must stay non-zero,
    // since the breath divides by it.
    selected: { alpha: 0.4, depth: 0, periodMs: 1000 },
}

let tuning: StatusHaloTuning = { ...DEFAULT_STATUS_HALO_TUNING }

export const statusHaloTuning = (): StatusHaloTuning => tuning

// WHICH STATES GET ONE. Only what the player must act on -- the three alarms, plus hunger while the
// member is walking off its post to deal with it.
//
// `idle` is pointedly absent even though the strip warns about it. Most of a camp is idle most of the
// time; a halo under all of them would make the cue mean "a camp exists" and would teach players to
// stop seeing it, which costs the three states that matter. The rest (fighting, foraging, defending,
// following, holding, feedingFire, hauling) are a member doing its job, which is not a problem.
export const STATUS_HALO_TIERS: Partial<Record<EmberMemberStatus, StatusHaloTier>> = {
    awaitingOrders: "alarm",
    needsFood: "alarm",
    needsJob: "alarm",
    hungry: "warn",
    wantsFood: "warn",
}

export const statusHaloTier = (status?: EmberMemberStatus | null): StatusHaloTier | undefined =>
    status ? STATUS_HALO_TIERS[status] : undefined

// Every halo currently on screen, so console tuning repaints what is already there instead of only
// reaching members who walk into view afterwards. Entries leave in dispose().
const liveHalos = new Set<MemberStatusHalo>()

// ONE CLOCK FOR THE WHOLE CAMP, so every halo breathes in step -- two members in trouble beating
// against each other reads as two separate alarms rather than one camp needing attention (the same
// reason the roster's cards share a single `pulseMs`). It is also one ticker callback for the room
// instead of one per troubled body: the halos only read this phase, they never drive it.
let phaseMs = 0
let ticking = false

const advancePhase = () => {
    // Ticker.shared.deltaMS is wall-clock, so the breath keeps its period through a frame-rate dip
    // instead of slowing down with the renderer.
    phaseMs += Ticker.shared.deltaMS
    liveHalos.forEach(halo => halo.applyPhase())
}

// Started by the first halo that actually lights up and stopped when the last one goes dark, so a
// room with nothing wrong in it carries no per-frame work at all.
const ensureTicking = () => {
    if (!ticking) {
        Ticker.shared.add(advancePhase)
        ticking = true
    }
}

const stopTickingIfIdle = () => {
    if (ticking && !Array.from(liveHalos).some(halo => halo.isLit())) {
        Ticker.shared.remove(advancePhase)
        ticking = false
    }
}

/** The breathing multiplier for a tier at the shared phase: 0 at the trough, 1 at the peak. */
export const haloBreath = (tier: TierTuning, atMs: number = phaseMs): number =>
    0.5 + 0.5 * Math.cos((atMs / tier.periodMs) * Math.PI * 2)

export const tuneStatusHalo = (next: Partial<StatusHaloTuning> | boolean): StatusHaloTuning => {
    const patch = typeof next === "boolean" ? { enabled: next } : next || {}
    tuning = {
        ...tuning,
        ...patch,
        warn: { ...tuning.warn, ...(patch.warn || {}) },
        alarm: { ...tuning.alarm, ...(patch.alarm || {}) },
        selected: { ...tuning.selected, ...(patch.selected || {}) },
    }
    liveHalos.forEach(halo => halo.redraw())
    return tuning
}

/** Reset to the shipped look. Exists for tests, which share module state with each other. */
export const resetStatusHaloTuning = (): StatusHaloTuning => tuneStatusHalo({ ...DEFAULT_STATUS_HALO_TUNING })

export class MemberStatusHalo extends Graphics {
    private tier?: StatusHaloTier
    // Which colour the geometry currently holds, or undefined while nothing has been drawn yet.
    // Geometry is drawn lazily -- every grounded body builds one of these so that it lands in the
    // right place in the child order (see the constructor comment in LpcCompositeSprite), and in a
    // camp of six, five are never lit -- and then redrawn only when the COLOUR has to change, which
    // is a member going from a problem disc to the selection disc or back. The breath animates
    // `alpha` alone: no clear(), no redraw, nothing per frame that touches geometry.
    private drawnColor?: number

    constructor() {
        super()
        this.visible = false
        liveHalos.add(this)
    }

    isLit(): boolean {
        return this.tier !== undefined
    }

    /** Light the halo at a tier, or pass nothing to put it out. */
    setTier(tier?: StatusHaloTier): void {
        if (this.tier === tier) {
            return
        }
        this.tier = tier
        if (!tier) {
            this.visible = false
            stopTickingIfIdle()
            return
        }
        this.redraw()
        ensureTicking()
        // Land on the current phase immediately rather than at the next frame, so a halo that lights
        // up never shows a single frame at the wrong opacity.
        this.applyPhase()
    }

    redraw(): void {
        this.visible = tuning.enabled && this.tier !== undefined
        if (!this.visible) {
            return
        }
        const wanted = this.tier === "selected" ? tuning.selectedColor : tuning.color
        if (this.drawnColor !== wanted) {
            this.drawGeometry()
        }
        this.applyPhase()
    }

    private drawGeometry(): void {
        this.clear()
        const { scale } = tuning
        const color = this.tier === "selected" ? tuning.selectedColor : tuning.color
        // Outer bloom first, then the core on top of it: two flat fills whose overlap reads as a
        // soft edge. Both are drawn at full opacity and the whole Graphics is faded by `alpha`, so
        // the breath is a single property write.
        this.beginFill(color, HALO_ALPHA_SHARE)
        this.drawEllipse(0, 0, HALO_RX * scale, HALO_RY * scale)
        this.endFill()
        this.beginFill(color, 1)
        this.drawEllipse(0, 0, CORE_RX * scale, CORE_RY * scale)
        this.endFill()
        this.drawnColor = color
    }

    /** Set this frame's opacity from the shared phase. The only thing the breath touches. */
    applyPhase(): void {
        if (!this.tier || !this.visible) {
            return
        }
        const tier = tuning[this.tier]
        this.alpha = tier.alpha + tier.depth * (haloBreath(tier) * 2 - 1)
    }

    // Decoration must never resize the thing it decorates. A renderable derives its on-screen y from
    // its own measured height (BaseRenderable.getYOffset reads `this.height`, a bounds query over
    // every child), so a halo that widened those bounds would nudge the very body it marks. Same
    // reasoning as PlayerRing and the hover outline's BoundlessContainer.
    calculateBounds(): void {
        this._bounds.clear()
    }

    // Drop the live-tuning registration. The owning sprite calls this from its cleanup, which runs on
    // a paperdoll REBUILD as well as on teardown -- and a rebuild is the common case here, because
    // the hungry brain swap re-stamps appearance -- so without this the set would grow by one entry
    // per rebuild and the shared ticker would never be able to stop.
    dispose(): void {
        liveHalos.delete(this)
        stopTickingIfIdle()
    }

    destroy(options?: any): void {
        this.dispose()
        super.destroy(options)
    }
}

/** Test seam: the shared breath phase is module state, like the tuning. */
export const resetStatusHaloClock = () => {
    phaseMs = 0
}

/**
 * Test seam: put out and deregister every live halo, so one case's lit halo cannot hold the shared
 * ticker open across the next one. The live set and the ticker are module state that tests share
 * with each other, exactly like the tuning.
 */
export const resetStatusHalos = () => {
    Array.from(liveHalos).forEach(halo => halo.dispose())
    resetStatusHaloClock()
}

// Browser-console tuning, in the same shape as window.playerRing / window.spriteShadow:
//
//     statusHalo = false                          // off
//     statusHalo = { scale: 1.4 }                 // wider footprint
//     statusHalo = { warn: { alpha: 0.35 } }      // make the quiet tier more present
//     statusHalo = { alarm: { periodMs: 2000 } }  // calm the alarm's breath down
//     statusHalo                                  // read the active values back
//
// Guarded so it is a no-op in the Node test harness.
if (typeof window !== "undefined") {
    Object.defineProperty(window, "statusHalo", {
        get: () => tuning,
        set: (next: Partial<StatusHaloTuning> | boolean) => {
            console.log("[statusHalo]", tuneStatusHalo(next))
        },
        configurable: true,
    })
}
